/**
 * The frame every page outside the table sits in.
 *
 * Before this each page invented its own top bar, so the room had three
 * different headers, two different ways home, and no mark on any of them. A
 * player moving between the lobby, the ranking and their profile had no way of
 * telling they were still in the same place.
 *
 * The brand book asks for a broadcast rather than an app: the mark on the left
 * the way a channel carries its own, the section named in the display face,
 * and a thin gold rule under it. That is the whole device, and it is enough —
 * the pages differ in what they say, not in how they are framed.
 */

import type React from 'react';

import { CardIcon } from '../brand.tsx';

export interface NavItem {
  readonly href: string;
  readonly label: string;
}

const NAV: readonly NavItem[] = [
  { href: '/', label: 'Lobby' },
  { href: '/leaderboard', label: 'Ranking' },
  { href: '/profile', label: 'My Profile' },
  { href: '/history', label: 'Hands' },
  { href: '/download', label: 'Get the App' },
];

export function SiteHeader({ here, right }: { here: string; right?: React.ReactNode }) {
  return (
    <header className="site-head">
      <a className="site-mark" href="/" aria-label="Iran Poker Tour">
        <CardIcon className="site-mark-icon" />
        <span className="site-mark-word lat-ui">
          Iran Poker <b>Tour</b>
        </span>
      </a>

      <nav className="site-nav">
        {NAV.map((item) => (
          <a key={item.href} href={item.href} className={item.href === here ? 'on' : ''}>
            {item.label}
          </a>
        ))}
      </nav>

      <span className="spacer" />
      {right}
    </header>
  );
}

/**
 * The name of the page, and one line about it.
 *
 * Uppercase in the display face with a gold rule under it, which is the book's
 * own heading device — the only place gold is allowed to be a line rather than
 * a word.
 */
export function PageTitle({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-title">
      {eyebrow !== undefined && <div className="eyebrow lat-ui">{eyebrow}</div>}
      <h1>{title}</h1>
      {children !== undefined && <p className="lead">{children}</p>}
    </div>
  );
}
